// src/App.js
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import MainLayout from "./layout/MainLayout";
import Dashboard from "./pages/Dashboard";
import OnusRegistradas from "./pages/OnusRegistradas";
import OnusOLT from "./pages/OnusOLT";
import OltConfig from "./pages/OltConfig";
import OltDetalle from "./pages/OltDetalle";
import OnusNoAutorizadas from "./pages/OnusNoAutorizadas";
import Clientes from "./pages/Clientes";
import MikrotikConfigCard from "./pages/MikrotikConfigCard";
import Planes from "./pages/Planes";
import Caja from "./pages/Caja";
import Empresa from "./pages/Empresa";
import Pagos from "./pages/Pagos";
import EstadoCuentaPDF from "./pages/EstadoCuentaPDF";
import Usuarios from "./pages/Usuarios";
import Cortes from "./pages/Cortes";
import Login from "./pages/Login";
import NoPermisos from "./pages/NoPermisos";
import ProtectedRoute from "./components/ProtectedRoute";
import Toast from "./components/Toast";
import CortesHistorial from "./pages/CortesHistorial";
import Respaldos from "./pages/Respaldos";
import Soporte from "./pages/Soporte";
import Switches from "./pages/Switches";
import SwitchDetalle from "./pages/SwitchDetalle";
// El mapa trae Leaflet y Geoman: se carga aparte, solo cuando se abre, para
// no hacer más pesada la app para quien nunca entra a esa pantalla.
const PlantaExterna = lazy(() => import("./pages/planta/PlantaExterna"));


export default function App() {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    function showToast(e) {
      setToast(e.detail);
    }
    window.addEventListener("toast", showToast);
    return () => window.removeEventListener("toast", showToast);
  }, []);

  return (
    <BrowserRouter>
      {/* TOAST GLOBAL */}
      {toast && (
        <Toast
          mensaje={toast.mensaje}
          tipo={toast.tipo}
          onClose={() => setToast(null)}
        />
      )}

      <Routes>
        {/* PUBLIC */}
        <Route path="/login" element={<Login />} />
        <Route path="/no-permisos" element={<NoPermisos />} />

        {/* PRIVATE layout */}
        <Route
          element={
            <ProtectedRoute roles={["admin", "caja", "tecnicos"]}>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />

          {/* ONUs / OLT (admin + tecnicos) */}
          <Route
            path="/onus-registradas"
            element={
              <ProtectedRoute roles={["admin", "tecnicos", "caja"]}>
                <OnusRegistradas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onus-olt"
            element={
              <ProtectedRoute roles={["admin", "tecnicos", "caja"]}>
                <OnusOLT />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onus-no-autorizadas"
            element={
              <ProtectedRoute roles={["admin", "tecnicos", "caja"]}>
                <OnusNoAutorizadas />
              </ProtectedRoute>
            }
          />

          {/* SWITCHES (admin + tecnicos) */}
          <Route
            path="/switches"
            element={
              <ProtectedRoute roles={["admin", "tecnicos"]}>
                <Switches />
              </ProtectedRoute>
            }
          />

          <Route
            path="/planta"
            element={
              <ProtectedRoute roles={["admin", "tecnicos"]}>
                <Suspense fallback={<p className="sw-nota sw-nota--centrada">Cargando el mapa…</p>}>
                  <PlantaExterna />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/switches/:id"
            element={
              <ProtectedRoute roles={["admin", "tecnicos"]}>
                <SwitchDetalle />
              </ProtectedRoute>
            }
          />

          <Route
            path="/olts/:id"
            element={
              <ProtectedRoute roles={["admin", "tecnicos"]}>
                <OltDetalle />
              </ProtectedRoute>
            }
          />

          {/* CLIENTES (todos) */}
          <Route path="/clientes" element={<Clientes />} />
          
          {/* CONFIG (solo admin) */}
          <Route
            path="/olt-config"
            element={
              <ProtectedRoute roles={["admin"]}>
                <OltConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mikrotik"
            element={
              <ProtectedRoute roles={["admin"]}>
                <MikrotikConfigCard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planes"
            element={
              <ProtectedRoute roles={["admin"]}>
                <Planes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/empresa"
            element={
              <ProtectedRoute roles={["admin"]}>
                <Empresa />
              </ProtectedRoute>
            }
          />

          {/* CAJA (admin + caja) */}
          <Route
            path="/caja"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <Caja />
              </ProtectedRoute>
            }
          />

          {/* PAGOS (admin + caja) */}
          <Route
            path="/pagos"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <Pagos />
              </ProtectedRoute>
            }
          />

          <Route
            path="/estado-cuenta"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <EstadoCuentaPDF />
              </ProtectedRoute>
            }
          />

          {/* CORTES (admin + caja) */}
          <Route
            path="/cortes"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <Cortes />
              </ProtectedRoute>
            }
          />

          {/* CORTES (admin + caja) */}
          <Route
            path="/corteshistorial"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <CortesHistorial />
              </ProtectedRoute>
            }
          />

          {/* SOPORTE — chat en vivo con clientes (admin + caja) */}
          <Route
            path="/soporte"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <Soporte />
              </ProtectedRoute>
            }
          />

          {/* RESPALDOS DE EQUIPOS (solo admin) */}
          <Route
            path="/respaldos"
            element={
              <ProtectedRoute roles={["admin"]}>
                <Respaldos />
              </ProtectedRoute>
            }
          />

          {/* Usuarios (admin + caja) */}
          <Route
            path="/usuarios"
            element={
              <ProtectedRoute roles={["admin", "caja"]}>
                <Usuarios />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

